import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy
} from "@angular/core";
import { NonNullableFormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { PhoneService } from "../../../../../common/services/api/resources/phone/phone.service";
import { PhoneEditRequestParams } from "../../../../../common/services/api/resources/phone/phone";
import { PhoneFeatures } from "../../../../../common/interfaces/phone-info";
import timezones from "../../../../../common/interfaces/timezones";
import { PhoneItemInfo } from "../phone-admin-page.component";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";

@Component({
  selector: "onsip-phone-info-edit",
  templateUrl: "./phone-info-edit.component.html",
  styleUrls: ["./phone-info-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhoneInfoEditComponent implements OnInit, OnDestroy {
  @Input() phoneInfo!: PhoneItemInfo;
  @Input() phoneFeatures!: PhoneFeatures;
  @Input() noFeatures!: boolean;
  @Output() viewPanel = new EventEmitter<void>();

  timezoneOptions = timezones.timezoneOptions;

  formGroup = this.fb.group({
    timezone: this.fb.control("0"),
    directory: this.fb.control(false),
    intercom: this.fb.control(false),
    natKeepalive: this.fb.control(false),
    easyTransfer: this.fb.control(false),
    webInterface: this.fb.control(false),
    endToEndEncryption: this.fb.control(false),
    digitMap: this.fb.control("")
  });

  loadingStateComplete$ = new BehaviorSubject(true);

  private unsubscriber = new Subscription();

  constructor(
    private fb: NonNullableFormBuilder,
    private phoneService: PhoneService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.formGroup.setValue({
      timezone: (parseInt(this.phoneInfo.gmtOffset) / 3600).toString(),
      directory: this.phoneInfo.directory,
      intercom: this.phoneInfo.intercom,
      natKeepalive: this.phoneInfo.natKeepalive,
      easyTransfer: this.phoneInfo.easyTransfer,
      webInterface: this.phoneInfo.webInterface,
      endToEndEncryption: this.phoneInfo.endToEndEncryption,
      digitMap: this.phoneInfo.digitmap
    });
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  toggleInfoPanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  submit(): void {
    if (this.formGroup.valid) {
      this.loadingStateComplete$.next(false);
      const GmtOffset = this.formGroup.controls.timezone.value
        ? parseFloat(this.formGroup.controls.timezone.value) * 3600
        : 0;

      const params: PhoneEditRequestParams = {
        PhoneId: this.phoneInfo.phoneId,
        Make: this.phoneInfo.make,
        Model: this.phoneInfo.model,
        ManualWebPassword: this.phoneInfo.manualWebPassword,
        OrganizationWebPassword: this.phoneInfo.organizationWebPassword,
        GmtOffset,
        Directory: this.formGroup.controls.directory.value || false,
        EasyTransfer: this.formGroup.controls.easyTransfer.value || false,
        NatKeepalive: this.formGroup.controls.natKeepalive.value || false,
        WebInterface: this.formGroup.controls.webInterface.value || false,
        Intercom: this.formGroup.controls.intercom.value || false,
        EndToEndEncryption: this.formGroup.controls.endToEndEncryption.value || false,
        Digitmap: this.formGroup.controls.digitMap.value || ""
      };

      this.phoneService.phoneEdit(params).then(result => {
        if (result.status === "success") {
          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
            "success"
          );
          this.viewPanel.emit();
        } else {
          this.snackbar.openSnackBar(result.data.message, "error");
          this.phoneService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
    }
  }
}
